import { createSlice } from '@reduxjs/toolkit';

type InitialState = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  gender: string;
  age_group: string;
  country: string;
  nearest_city: string;
  nearest_airport: string;
  chosen_plan: number;
  primary_travel_purposes: string[];
  destination_types: string[];
  activity_interests: string[];
  travel_preference: string;
  budget_preference: string;
  pace_preference: string;
  is_inviting_others: boolean;
  unit_system: string;
  trip_planning_style: string;
  profile_picture: string | null;
  is_premium: boolean;
  premium_canceled_at_period_end: boolean;
  is_google_user: boolean;
  show_trip_budgets: boolean;
  referral_program_eligible: boolean;
};

const initialState: InitialState = {
  id: 0,
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  gender: '',
  age_group: '',
  country: '',
  nearest_city: '',
  nearest_airport: '',
  chosen_plan: 0,
  primary_travel_purposes: [],
  destination_types: [],
  activity_interests: [],
  travel_preference: '',
  budget_preference: '',
  pace_preference: '',
  is_inviting_others: false,
  unit_system: '',
  trip_planning_style: '',
  profile_picture: null,
  is_premium: false,
  premium_canceled_at_period_end: false,
  is_google_user: false,
  show_trip_budgets: true,
  referral_program_eligible: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserInfo: (state, { payload }) => {
      const {
        id,
        first_name,
        last_name,
        email,
        username,
        gender,
        age_group,
        country,
        nearest_city,
        nearest_airport,
        chosen_plan,
        primary_travel_purposes,
        destination_types,
        activity_interests,
        travel_preference,
        budget_preference,
        pace_preference,
        is_inviting_others,
        unit_system,
        trip_planning_style,
        profile_picture,
        is_premium,
        premium_canceled_at_period_end,
        is_google_user,
        show_trip_budgets,
        referral_program_eligible,
      } = payload;

      state.id = id ?? state.id;
      state.first_name = first_name ?? state.first_name;
      state.last_name = last_name ?? state.last_name;
      state.email = email ?? state.email;
      state.username = username ?? state.username;
      state.gender = gender ?? state.gender;
      state.age_group = age_group ?? state.age_group;
      state.country = country ?? state.country;
      state.nearest_city = nearest_city ?? state.nearest_city;
      state.nearest_airport = nearest_airport ?? state.nearest_city;
      state.chosen_plan = chosen_plan ?? state.chosen_plan;
      state.primary_travel_purposes = primary_travel_purposes ?? state.primary_travel_purposes;
      state.destination_types = destination_types ?? state.destination_types;
      state.activity_interests = activity_interests ?? state.activity_interests;
      state.travel_preference = travel_preference ?? state.travel_preference;
      state.budget_preference = budget_preference ?? state.budget_preference;
      state.pace_preference = pace_preference ?? state.pace_preference;
      state.is_inviting_others = is_inviting_others ?? state.is_inviting_others;
      state.unit_system = unit_system ?? state.unit_system;
      state.trip_planning_style = trip_planning_style ?? state.trip_planning_style;
      state.profile_picture = profile_picture ?? state.profile_picture;
      state.is_premium = is_premium ?? state.is_premium;
      state.premium_canceled_at_period_end =
        premium_canceled_at_period_end ?? state.premium_canceled_at_period_end;
      state.is_google_user = is_google_user ?? state.is_google_user;
      state.show_trip_budgets = show_trip_budgets ?? state.show_trip_budgets;
      state.referral_program_eligible = referral_program_eligible ?? state.referral_program_eligible;
    },
    logOutUser: state => {
      state.id = 0;
      state.first_name = '';
      state.last_name = '';
      state.email = '';
      state.username = '';
      state.gender = '';
      state.age_group = '';
      state.country = '';
      state.nearest_city = '';
      state.nearest_airport = '';
      state.chosen_plan = 0;
      state.primary_travel_purposes = [''];
      state.destination_types = [''];
      state.activity_interests = [''];
      state.travel_preference = '';
      state.budget_preference = '';
      state.pace_preference = '';
      state.is_inviting_others = false;
      state.unit_system = '';
      state.trip_planning_style = '';
      state.profile_picture = null;
      state.is_premium = false;
      state.premium_canceled_at_period_end = false;
      state.is_google_user = false;
      state.referral_program_eligible = false;
    },
  },
});

export const { setUserInfo, logOutUser } = userSlice.actions;

export default userSlice.reducer;
